import { useEffect, useRef, useState } from "react";

import {
  is2FAEnabled,
  reAuthenticate,
  verifyAndRemove2FA,
  resolveMFAChallenge,
  sendEnrollmentCode,
  verifyAndAdd2FA,
} from "@/services/auth2FA";
import { useAuthStore } from "@/store/auth.store";
import { t } from "i18next";
import { FirebaseError } from "firebase/app";
import type { MultiFactorError, MultiFactorResolver } from "firebase/auth";

export enum Validate2FAStep {
  Idle = "idle",
  enterPassword = "enterPassword",
  VerfifySMS = "verifySMS",
}

export const useValidate2FA = (): {
  enabled: boolean;
  step: Validate2FAStep;
  toggle2FA: () => void;
  confirmPassword: (password: string) => Promise<void>;
  confirmVerificationCode: (code: string) => Promise<void>;
  error: string | null;
  loading: boolean;
  close2FADialog: () => void;
} => {
  const { userData } = useAuthStore.getState();
  const resolver = useRef<MultiFactorResolver | null>(null);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [verificationId, setVerificationId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<Validate2FAStep>(Validate2FAStep.Idle);

  useEffect(() => {
    const check2FA = async (): Promise<void> => {
      const result = await is2FAEnabled();
      setEnabled(result);
    };
    void check2FA();
  }, []);

  const close2FADialog = (): void => {
    setStep(Validate2FAStep.Idle);
    setLoading(false);
  };

  const toggle2FA = (): void => {
    setStep(Validate2FAStep.enterPassword);
  };

  const confirmPassword = async (password: string): Promise<void> => {
    try {
      setLoading(true);
      await reAuthenticate(password);
      if (enabled) {
        setStep(Validate2FAStep.VerfifySMS);
      } else {
        if (userData?.phone == null || userData?.phone == "")
          throw new Error(
            t(
              "settings.account.components.switchAuthentication.error.phoneIsRequired"
            )
          );
        const id = await sendEnrollmentCode(userData.phone);
        setVerificationId(id);
        setStep(Validate2FAStep.VerfifySMS);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === "auth/multi-factor-auth-required") {
          const result = await resolveMFAChallenge(error as MultiFactorError);
          resolver.current = result.resolver;
          setVerificationId(result.verificationId);
          setStep(Validate2FAStep.VerfifySMS);
        } else {
          if (error.code == "auth/unverified-email") {
            setError(t("error.auth.unverifiedEmail"));
          } else if (error.code == "auth/invalid-credential") {
            setError(t("error.auth.invalidCredential"));
          } else {
            setError(t("common.error"));
          }
        }
      } else {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("common.error"));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmVerificationCode = async (code: string): Promise<void> => {
    try {
      setLoading(true);
      if (enabled) {
        if (resolver.current) {
          await verifyAndRemove2FA(resolver.current, verificationId, code);
        }
        setEnabled(false);
        setStep(Validate2FAStep.Idle);
      } else {
        await verifyAndAdd2FA(verificationId, code);
        setEnabled(true);
        setStep(Validate2FAStep.Idle);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code == "auth/invalid-verification-code") {
          setError(t("error.auth.invalidVerificationCode"));
        } else {
          setError(t("common.error"));
        }
      } else {
        setError(t("common.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    enabled,
    step,
    toggle2FA,
    confirmPassword,
    confirmVerificationCode,
    error,
    loading,
    close2FADialog,
  };
};
